import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Brain, TrendingUp, AlertTriangle, Lightbulb, Calendar, Target, Zap } from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/config';

const API = `${BACKEND_URL}/api`;

const KPIAIInsights = ({ filters }) => {
  const [aiData, setAiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forecastMonths, setForecastMonths] = useState(6);

  useEffect(() => {
    fetchAIInsights();
  }, [filters, forecastMonths]);

  const fetchAIInsights = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      
      const response = await axios.get(
        `${API}/ai/insights?year=${filters.year}&forecast_months=${forecastMonths}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiData(response.data);
      
    } catch (error) {
      console.error('Error fetching AI insights:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !aiData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
        <p className="text-gray-600">🤖 AI sta analizzando i tuoi dati...</p>
      </div>
    );
  }

  // Prepare chart data
  const occupancyChartData = (aiData.occupancy_forecast || []).map(item => ({
    date: new Date(item.ds).toLocaleDateString('it-IT', { month: 'short', day: 'numeric' }),
    forecast: Math.max(0, item.yhat),
    lower: Math.max(0, item.yhat_lower),
    upper: item.yhat_upper
  }));

  const revenueChartData = (aiData.revenue_forecast || []).map(item => ({
    date: new Date(item.ds).toLocaleDateString('it-IT', { month: 'short', day: 'numeric' }),
    forecast: Math.max(0, item.yhat),
    lower: Math.max(0, item.yhat_lower),
    upper: item.yhat_upper
  }));

  // Sample every N days for better visualization
  const sampleData = (data, step = 7) => {
    return data.filter((_, index) => index % step === 0);
  };

  const insights = aiData.ai_insights || {
    breve_termine: [],
    medio_termine: [],
    lungo_termine: [],
    alert: [],
    opportunita: []
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
            <Brain className="h-8 w-8 text-purple-600 mr-3" />
            🤖 AI Insights & Forecasting
          </h2>
          <p className="text-gray-600">Analisi predittiva e suggerimenti strategici generati dall'intelligenza artificiale</p>
        </div>
        <div className="flex gap-2">
          {[3, 6, 12].map(months => (
            <Button
              key={months}
              variant={forecastMonths === months ? "default" : "outline"}
              onClick={() => setForecastMonths(months)}
              size="sm"
            >
              {months} mesi
            </Button>
          ))}
        </div>
      </div>

      {/* Current Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-700">Ricavi Anno {filters.year}</p>
              <p className="text-3xl font-bold text-blue-900">
                €{aiData.current_stats.revenue.toLocaleString('it-IT', {minimumFractionDigits: 2})}
              </p>
            </div>
            <TrendingUp className="h-12 w-12 text-blue-600" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-700">Notti Vendute</p>
              <p className="text-3xl font-bold text-green-900">
                {aiData.current_stats.nights.toLocaleString()}
              </p>
            </div>
            <Calendar className="h-12 w-12 text-green-600" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-700">ADR Medio</p>
              <p className="text-3xl font-bold text-purple-900">
                €{(aiData.current_stats.revenue / aiData.current_stats.nights).toFixed(2)}
              </p>
            </div>
            <Target className="h-12 w-12 text-purple-600" />
          </div>
        </Card>
      </div>

      {/* Alerts & Opportunities */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Alerts */}
        <Card className="p-6 border-l-4 border-red-500">
          <h3 className="text-lg font-bold text-red-900 mb-4 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            ⚠️ Alert Critici
          </h3>
          <ul className="space-y-3">
            {insights.alert.map((alert, index) => (
              <li key={index} className="flex items-start">
                <div className="h-2 w-2 rounded-full bg-red-500 mt-2 mr-3 flex-shrink-0"></div>
                <p className="text-gray-700">{alert}</p>
              </li>
            ))}
          </ul>
        </Card>

        {/* Opportunities */}
        <Card className="p-6 border-l-4 border-green-500">
          <h3 className="text-lg font-bold text-green-900 mb-4 flex items-center">
            <Lightbulb className="h-5 w-5 mr-2" />
            💡 Opportunità
          </h3>
          <ul className="space-y-3">
            {insights.opportunita.map((opp, index) => (
              <li key={index} className="flex items-start">
                <div className="h-2 w-2 rounded-full bg-green-500 mt-2 mr-3 flex-shrink-0"></div>
                <p className="text-gray-700">{opp}</p>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Forecasting Charts */}
      {occupancyChartData.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            📊 Forecast Occupazione - Prossimi {forecastMonths} Mesi
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={sampleData(occupancyChartData)}>
              <defs>
                <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                stroke="#6b7280" 
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="#6b7280" 
                style={{ fontSize: '12px' }}
                label={{ value: 'Notti', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                formatter={(value) => [`${value.toFixed(1)} notti`, '']}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
              <Area 
                type="monotone" 
                dataKey="forecast" 
                stroke="#8b5cf6" 
                fillOpacity={1} 
                fill="url(#colorForecast)"
                name="Previsione"
              />
            </AreaChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-500 mt-2 text-center">
            * Previsione basata su algoritmi di machine learning (Prophet) analizzando pattern storici
          </p>
        </Card>
      )}

      {revenueChartData.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            💰 Forecast Ricavi - Prossimi {forecastMonths} Mesi
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={sampleData(revenueChartData)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                stroke="#6b7280" 
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="#6b7280" 
                style={{ fontSize: '12px' }}
                label={{ value: 'Ricavi (€)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                formatter={(value) => [`€${value.toFixed(2)}`, '']}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="forecast" 
                stroke="#10b981" 
                strokeWidth={3}
                dot={false}
                name="Previsione Ricavi"
              />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-500 mt-2 text-center">
            * Stima basata su trend storici e stagionalità identificati dall'AI
          </p>
        </Card>
      )}

      {/* Strategic Suggestions */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <Zap className="h-6 w-6 text-yellow-500 mr-2" />
          🎯 Suggerimenti Strategici AI
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Short Term */}
          <Card className="p-6 bg-gradient-to-br from-yellow-50 to-yellow-100">
            <h4 className="text-lg font-bold text-yellow-900 mb-4 flex items-center">
              <Target className="h-5 w-5 mr-2" />
              Breve Termine (1-3 mesi)
            </h4>
            <ul className="space-y-3">
              {insights.breve_termine.map((sug, index) => (
                <li key={index} className="flex items-start">
                  <div className="h-2 w-2 rounded-full bg-yellow-600 mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-sm text-gray-700">{sug}</p>
                </li>
              ))}
            </ul>
          </Card>

          {/* Medium Term */}
          <Card className="p-6 bg-gradient-to-br from-orange-50 to-orange-100">
            <h4 className="text-lg font-bold text-orange-900 mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              Medio Termine (3-6 mesi)
            </h4>
            <ul className="space-y-3">
              {insights.medio_termine.map((sug, index) => (
                <li key={index} className="flex items-start">
                  <div className="h-2 w-2 rounded-full bg-orange-600 mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-sm text-gray-700">{sug}</p>
                </li>
              ))}
            </ul>
          </Card>

          {/* Long Term */}
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100">
            <h4 className="text-lg font-bold text-blue-900 mb-4 flex items-center">
              <Brain className="h-5 w-5 mr-2" />
              Lungo Termine (6-12 mesi)
            </h4>
            <ul className="space-y-3">
              {insights.lungo_termine.map((sug, index) => (
                <li key={index} className="flex items-start">
                  <div className="h-2 w-2 rounded-full bg-blue-600 mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-sm text-gray-700">{sug}</p>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      {/* AI Info Box */}
      <Card className="p-6 bg-gradient-to-r from-purple-100 to-pink-100 border-purple-300">
        <div className="flex items-start gap-4">
          <Brain className="h-8 w-8 text-purple-600 flex-shrink-0 mt-1" />
          <div>
            <h4 className="font-bold text-purple-900 mb-2">🤖 Come funziona l'AI</h4>
            <p className="text-sm text-purple-800 mb-2">
              Il sistema utilizza algoritmi di machine learning (Facebook Prophet) per analizzare i tuoi dati storici e identificare pattern, trend e stagionalità.
            </p>
            <p className="text-sm text-purple-800">
              I suggerimenti strategici sono generati da GPT-4o di OpenAI, analizzando le previsioni e il contesto del tuo business per fornire raccomandazioni actionable e personalizzate.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default KPIAIInsights;
